<script setup>
import {
  Book,
  Calendar,
  HelpCircle,
  Home,
  Info,
  Settings,
  X,
} from 'lucide-vue-next'
import { useRoute } from 'vue-router'
import { computed } from 'vue'

// Emit close event when a menu item is clicked
const emit = defineEmits(['close'])

const route = useRoute()

// Check if the current route is active
function isActive(path) {
  if (path === '/' && route.path === '/') {
    return true
  }
  return path !== '/' && route.path.startsWith(path)
}

function closeMenu() {
  emit('close')
}

// Menu items configuration
const mainMenuItems = [
  { path: '/', icon: Home, label: 'Accueil' },
  { path: '/bibli', icon: Book, label: 'Bibliothèque' },
  { path: '/meet', icon: Calendar, label: 'Rendez-vous' },
  // { path: "/client", icon: Users, label: "Clients" },
  { path: '/welcome', icon: Book, label: 'Guide de démarrage' },

  // { path: "/blog", icon: Book, label: "Blog" },
]

const settingsMenuItems = [
  { path: '/settings', icon: Settings, label: 'Paramètres' },
  { path: '/help', icon: HelpCircle, label: 'Aide' },
]

const footerMenuItems = [
  { path: '/terms', icon: Info, label: 'Termes et conditions' },
  { path: '/privacy', icon: Info, label: 'Politique de confidentialité' },
]

// Computed property to generate menu items with active class
function getMenuItems(items) {
  return items.map(item => ({
    ...item,
    active: isActive(item.path),
    class: `flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors ${
      isActive(item.path) ? 'bg-accent' : ''
    }`,
  }))
}

const computedMainMenuItems = computed(() => getMenuItems(mainMenuItems))
const computedSettingsMenuItems = computed(() =>
  getMenuItems(settingsMenuItems),
)
const computedFooterMenuItems = computed(() =>
  footerMenuItems.map(item => ({
    ...item,
    class: `flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors text-sm text-muted-foreground`,
  })),
)
</script>

<template>
  <div class="mobile-menu fixed inset-0 z-50 bg-background/95 backdrop-blur-sm">
    <!-- Header with close button -->
    <div
      class="flex items-center justify-between p-4 border-b"
      @click="closeMenu"
    >
      <h2 class="text-xl font-semibold">
        Menu
      </h2>
      <button
        class="p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label="Close menu"
        @click="closeMenu"
      >
        <X class="h-5 w-5" />
      </button>
    </div>

    <!-- Menu Items -->
    <div class="p-4 space-y-6">
      <!-- Main Navigation -->
      <div class="space-y-2">
        <RouterLink
          v-for="item in computedMainMenuItems"
          :key="item.path"
          :to="item.path"
          :class="item.class"
          @click="closeMenu"
        >
          <component :is="item.icon" class="h-5 w-5" />
          <span>{{ item.label }}</span>
        </RouterLink>
      </div>

      <!-- Settings Section -->
      <div class="pt-4 border-t">
        <RouterLink
          v-for="item in computedSettingsMenuItems"
          :key="item.path"
          :to="item.path"
          :class="item.class"
          @click="closeMenu"
        >
          <component :is="item.icon" class="h-5 w-5" />
          <span>{{ item.label }}</span>
        </RouterLink>
      </div>

      <!-- Footer Links -->
      <div class="pt-4 border-t">
        <RouterLink
          v-for="item in computedFooterMenuItems"
          :key="item.path"
          :to="item.path"
          :class="item.class"
          @click="closeMenu"
        >
          <component :is="item.icon" class="h-4 w-4" />
          <span>{{ item.label }}</span>
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mobile-menu {
  animation: slide-in 0.3s ease forwards;
}

@keyframes slide-in {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

/* Dark mode support */
:root.dark .hover\:bg-gray-100:hover {
  background-color: hsl(var(--muted));
}
</style>
